import { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { API_GET, API_PATCH, DBRequest, useConfig } from '../../api/api';
import useFetch from '../../api/useFetch';
import ExoAvatar from '../../components/exo/ExoAvatar';
import { ExoSkeleton } from '../../components/exo/ExoSkeleton';

/**
 * Settings tab "Calculative hourly rates": every employee with the global
 * hourly rate (wage preset) they are assigned to. A change is saved right
 * away (PATCH users/{id} globalWagePresetId); the backend keeps the project
 * copies of the rate in sync.
 */
const HourlyRateAssignments = ({ onFeedback = () => {} }) => {
  const { t } = useTranslation();
  const config = useConfig();
  const currencySymbol = config?.currency?.currencySymbol ?? '€';

  const { apiData: presets } = useFetch(
    'wage-presets?isGlobal[eq]=1&perPage=200',
  );
  const [users, setUsers] = useState(null);
  // user id -> preset id, taken from the users of each global preset
  const [assignments, setAssignments] = useState({});
  const [savingIds, setSavingIds] = useState([]);

  useEffect(() => {
    if (!config) return;
    DBRequest({
      config,
      path: 'users',
      method: API_GET,
      onResponse: (res) => setUsers(res.data || []),
    });
  }, [config]);

  useEffect(() => {
    if (!presets?.data) return;
    const map = {};
    presets.data.forEach((preset) => {
      (preset.users || []).forEach((user) => {
        map[user.id] = preset.id;
      });
    });
    setAssignments(map);
  }, [presets]);

  function handleChange(user, presetId) {
    const previous = assignments[user.id] ?? '';
    setAssignments((prev) => ({ ...prev, [user.id]: presetId }));
    setSavingIds((prev) => [...prev, user.id]);

    DBRequest({
      config,
      path: `users/${user.id}`,
      method: API_PATCH({ globalWagePresetId: presetId || null }),
      onResponse: () => {
        setSavingIds((prev) => prev.filter((id) => id !== user.id));
        onFeedback(t('Saved'), 'success');
      },
      onError: () => {
        setSavingIds((prev) => prev.filter((id) => id !== user.id));
        setAssignments((prev) => ({ ...prev, [user.id]: previous }));
        onFeedback(t('Could not save'), 'error');
      },
    });
  }

  if (!users || !presets) return <ExoSkeleton n={5} size="50px" />;

  const presetLabel = (preset) =>
    `${preset.label} (${preset.wage} ${currencySymbol}/h)`;

  return (
    <Box className="w-full h-full p-4 flex flex-col gap-3 overflow-y-auto">
      <Typography fontSize={13} className="opacity-70">
        {t(
          'Project hourly rates created from a global rate automatically include its employees.',
        )}
      </Typography>
      {presets.data.length === 0 && (
        <Typography className="opacity-60">
          {t('No global calculative hourly rates yet.')}
        </Typography>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('Employee')}</TableCell>
            <TableCell>{t('Calculative hourly rate')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell>
                <Box className="flex items-center gap-2">
                  <ExoAvatar
                    picture={user.picture ? user.picture.thumbnailUrl : ''}
                    name={`${user.firstName} ${user.lastName}`}
                    size="32px"
                    type="contact"
                  />
                  {`${user.firstName} ${user.lastName}`}
                </Box>
              </TableCell>
              <TableCell sx={{ width: '50%' }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={assignments[user.id] ?? ''}
                    displayEmpty
                    disabled={savingIds.includes(user.id)}
                    onChange={(e) => handleChange(user, e.target.value)}
                  >
                    <MenuItem value="">
                      <em>{t('No Select')}</em>
                    </MenuItem>
                    {presets.data.map((preset) => (
                      <MenuItem key={preset.id} value={preset.id}>
                        {presetLabel(preset)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default HourlyRateAssignments;
